import { useEffect, useState } from "react";
import { Bell, Home, Trophy, Users, User, Footprints, Flame } from "lucide-react";
// Cairo — the employee app's font (employee-app `src/config/clients.ts` athlynApp
// fonts, `app.json` expo-font). Self-hosted via @fontsource (OFL-1.1): no external
// font request. Only the weights the preview draws are imported, and only here.
import "@fontsource/cairo/400.css";
import "@fontsource/cairo/500.css";
import "@fontsource/cairo/600.css";
import "@fontsource/cairo/700.css";
import {
  WHITE,
  contrastRatio,
  deriveTertiary,
  effectiveAccent,
  isValidHex,
  readableFg,
  readableOn,
} from "@/lib/brandContrast";
import {
  HEADER_LOGO_BOX,
  HEADER_MIRROR,
  PREVIEW_PHONE_WIDTH,
  PREVIEW_STATUS_BAR,
} from "@/lib/logoFraming";

/**
 * The preview is drawn at the app's proportions on a standard 390-pt-wide phone
 * (D25): 1 CSS px = 1 pt. The home header is exact — every measurement comes
 * from `HEADER_MIRROR` / `HEADER_LOGO_BOX` / `PREVIEW_STATUS_BAR` (mirrored from
 * employee-app athlyn2); the content beneath it is indicative. These sizes and
 * the colours below are PREVIEW DATA describing the phone, not portal chrome —
 * the frame and captions around the phone use cms-portal tokens.
 */
export const PREVIEW_LABEL = "Drawn to the app's exact proportions on a standard phone (390 points wide)";

/** The app's font (preview data). */
export const PREVIEW_FONT_FAMILY = '"Cairo", sans-serif';

/** `#RRGGBB` + alpha ⇒ `rgba(...)` — for the header's translucent fills (preview data). */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Preview data mirroring the athlyn2 employee app's LIGHT palette neutrals
 * (employee-app `src/utils/theme.tsx` lightColors: surface, page background,
 * primary text, secondary text, border). These are NOT portal colours: they
 * exist only to draw the phone inside the preview frame, so they are
 * exempt from the portal design-token rule. Portal chrome around the phone uses
 * cms-portal tokens.
 */
export const PREVIEW_NEUTRALS = {
  surface: "#FFFFFF",
  page: "#F5F7F9",
  text: "#1A1A1A",
  grey: "#6B7280",
  border: "#E5E7EB",
} as const;

/**
 * The athlyn2 default brand colours — what employees see while branding is off
 * or until both organisation colours are set. Preview data, same exemption.
 */
export const PREVIEW_ATHLYN_DEFAULTS = {
  main: "#1C3446",
  highlight: "#008DA2",
} as const;

/** Text on a gradient: the candidate (white or ink) with the best worst-case contrast over all stops. */
function readableOnGradient(stops: string[], ink: string): string {
  const worst = (candidate: string) =>
    Math.min(...stops.map((stop) => contrastRatio(stop, candidate)));
  const candidates = Array.from(new Set(stops.map((s) => readableOn(s, ink))));
  if (candidates.length === 1) return candidates[0];
  return worst(ink) > worst(WHITE) ? ink : WHITE;
}

interface BrandPreviewPhoneProps {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  /** The "Brand the employee app" switch — only changes the caption. */
  enabled?: boolean;
  /** Square logo picked in the form but not uploaded yet — preferred over `logoUrl`. */
  logoFile?: File | null;
  /** The saved square logo URL. */
  logoUrl?: string | null;
  /** Wide (home header) logo picked but not uploaded yet — the header's first choice. */
  wideLogoFile?: File | null;
  /** The saved wide logo URL. */
  wideLogoUrl?: string | null;
  organizationName?: string;
}

export type HeaderLogoVariant = "wide" | "square";

/**
 * The logo the app's home header shows: wide (pending ?? saved), else square
 * (pending ?? saved). Empty URLs count as none. Shared with the logo check so
 * the warning always judges the logo the preview shows.
 */
export function pickHeaderLogo({
  wideLogoFile,
  wideLogoUrl,
  logoFile,
  logoUrl,
}: Pick<BrandPreviewPhoneProps, "wideLogoFile" | "wideLogoUrl" | "logoFile" | "logoUrl">):
  | { source: File | string; variant: HeaderLogoVariant }
  | null {
  if (wideLogoFile) return { source: wideLogoFile, variant: "wide" };
  if (wideLogoUrl) return { source: wideLogoUrl, variant: "wide" };
  if (logoFile) return { source: logoFile, variant: "square" };
  if (logoUrl) return { source: logoUrl, variant: "square" };
  return null;
}

export function BrandPreviewPhone({
  primaryColor,
  secondaryColor,
  accentColor,
  enabled = false,
  logoFile,
  logoUrl,
  wideLogoFile,
  wideLogoUrl,
  organizationName,
}: BrandPreviewPhoneProps) {
  const headerLogo = pickHeaderLogo({ wideLogoFile, wideLogoUrl, logoFile, logoUrl });
  const headerFile = headerLogo && typeof headerLogo.source !== "string" ? headerLogo.source : null;
  const [pendingLogoUrl, setPendingLogoUrl] = useState<string | null>(null);

  // Object URL only for the pending file the header actually shows.
  useEffect(() => {
    if (!headerFile) {
      setPendingLogoUrl(null);
      return;
    }
    const url = URL.createObjectURL(headerFile);
    setPendingLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [headerFile]);

  const coloursSet = isValidHex(primaryColor) && isValidHex(secondaryColor);
  const main = coloursSet ? (primaryColor as string) : PREVIEW_ATHLYN_DEFAULTS.main;
  const highlight = coloursSet ? (secondaryColor as string) : PREVIEW_ATHLYN_DEFAULTS.highlight;
  const accent = effectiveAccent(coloursSet ? accentColor : null, highlight) ?? highlight;
  const tertiary = deriveTertiary(main);

  const ink = PREVIEW_NEUTRALS.text;
  const onMain = readableOn(main, ink);
  const onHighlight = readableOn(highlight, ink);
  const onFeatured = readableOnGradient([main, tertiary], ink);
  const onAccent = readableOn(accent, ink);
  // Highlight drawn AS an icon/text on the white surface (C-FG): falls back to the
  // org's MAIN colour when too light — as the app passes brandInk, not the base ink.
  const highlightFg = readableFg(highlight, main, PREVIEW_NEUTRALS.surface);

  const logoSrc = headerLogo
    ? typeof headerLogo.source === "string"
      ? headerLogo.source
      : pendingLogoUrl
    : null;

  const tertiaryIconFill = withAlpha(tertiary, 0.3);
  const logoAlt = organizationName ? `${organizationName} logo` : "Organisation logo";

  return (
    <div className="min-w-0 space-y-2" data-testid="brand-preview">
      <p className="text-xs font-medium text-muted-foreground">{PREVIEW_LABEL}</p>

      {/* Below the phone's width the preview scrolls sideways rather than shrinking:
          shrinking would break the 1 px = 1 pt proportions it exists to show. */}
      <div data-testid="preview-scroll" className="overflow-x-auto pb-2">
        <div className="mx-auto w-fit overflow-hidden rounded-[2.75rem] border-[8px] border-foreground/90 bg-muted shadow-lg">
          <div
            data-testid="preview-phone"
            className="flex flex-col text-sm leading-snug"
            style={{
              width: PREVIEW_PHONE_WIDTH,
              fontFamily: PREVIEW_FONT_FAMILY,
              backgroundColor: PREVIEW_NEUTRALS.page,
              color: PREVIEW_NEUTRALS.text,
            }}
          >
            {/* Home header — EXACT (athlyn2 HomeScreenHeader, see HEADER_MIRROR). */}
            <div
              data-testid="preview-header"
              className="relative z-10"
              style={{
                backgroundColor: main,
                color: onMain,
                paddingTop: PREVIEW_STATUS_BAR + HEADER_MIRROR.topPadding,
                paddingLeft: HEADER_MIRROR.horizontalPadding,
                paddingRight: HEADER_MIRROR.horizontalPadding,
                paddingBottom: HEADER_MIRROR.bottomPadding,
                // athlyn2 header.shadow: brandShadow (= main) @ 0.12, offset 0 4, blur 16.
                boxShadow: `0 4px 16px ${withAlpha(main, 0.12)}`,
              }}
            >
              {/* Status-bar area (stand-in for insets.top) — indicative clock only. */}
              <div
                data-testid="preview-status-bar"
                aria-hidden="true"
                className="absolute inset-x-0 top-0 flex items-center px-8 text-[15px] font-semibold"
                style={{ height: PREVIEW_STATUS_BAR }}
              >
                9:41
              </div>

              <div className="flex items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center">
                  {/* Org logo directly on the main colour — no tile (D16) — in the app's
                      fixed box, contained + left-aligned; the square logo uses the same box. */}
                  <div
                    data-testid="preview-header-logo-box"
                    className="flex items-center"
                    style={{ width: HEADER_LOGO_BOX.width, height: HEADER_LOGO_BOX.height, flexShrink: 1 }}
                  >
                    {headerLogo && logoSrc ? (
                      <img
                        data-testid="preview-header-logo"
                        data-variant={headerLogo.variant}
                        src={logoSrc}
                        alt={logoAlt}
                        className="block"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          objectPosition: "left center",
                        }}
                      />
                    ) : (
                      <span
                        data-testid="preview-logo-placeholder"
                        className="text-base font-semibold leading-none"
                        style={{ color: onMain }}
                      >
                        Your logo
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <div
                    data-testid="preview-bell"
                    className="relative flex items-center justify-center rounded-full"
                    style={{
                      width: HEADER_MIRROR.iconSize,
                      height: HEADER_MIRROR.iconSize,
                      marginRight: HEADER_MIRROR.iconGap,
                      backgroundColor: tertiaryIconFill,
                    }}
                  >
                    <Bell style={{ width: 20, height: 20, color: onMain }} aria-hidden="true" />
                    <span
                      data-testid="preview-accent-dot"
                      className="absolute flex items-center justify-center rounded-full text-[10px] leading-none"
                      style={{
                        top: -2,
                        right: -2,
                        minWidth: 18,
                        height: 18,
                        paddingLeft: 4,
                        paddingRight: 4,
                        backgroundColor: accent,
                        color: onAccent,
                        border: `2px solid ${main}`,
                      }}
                    >
                      3
                    </span>
                  </div>
                  <div
                    data-testid="preview-avatar"
                    className="flex items-center justify-center rounded-full font-medium"
                    style={{
                      width: HEADER_MIRROR.iconSize,
                      height: HEADER_MIRROR.iconSize,
                      backgroundColor: tertiaryIconFill,
                      borderWidth: HEADER_MIRROR.avatarBorder,
                      borderStyle: "solid",
                      borderColor: withAlpha(onMain, 0.2),
                      color: onMain,
                      fontSize: 16,
                    }}
                  >
                    SJ
                  </div>
                </div>
              </div>
            </div>

            {/* Everything below the header is INDICATIVE — representative content only. */}
            <div data-testid="preview-indicative" data-indicative="true" className="space-y-3 px-5 py-4">
              <div>
                <p className="text-xs" style={{ color: PREVIEW_NEUTRALS.grey }}>
                  Good morning
                </p>
                <p className="text-lg font-bold">Sarah</p>
              </div>

              {/* Chips */}
              <div className="flex gap-2">
                <span
                  data-testid="preview-chip-selected"
                  className="rounded-full px-3 py-1 text-[13px] font-medium"
                  style={{ backgroundColor: highlight, color: onHighlight }}
                >
                  All
                </span>
                {["Running", "Yoga"].map((label) => (
                  <span
                    key={label}
                    className="rounded-full border px-3 py-1 text-[13px]"
                    style={{
                      backgroundColor: PREVIEW_NEUTRALS.surface,
                      borderColor: PREVIEW_NEUTRALS.border,
                      color: PREVIEW_NEUTRALS.grey,
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>

              {/* Featured challenge */}
              <div
                data-testid="preview-featured"
                className="rounded-2xl p-4"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${main}, ${tertiary})`,
                  color: onFeatured,
                }}
              >
                <p className="text-[11px] uppercase tracking-wide opacity-80">Featured challenge</p>
                <p className="mt-0.5 text-base font-semibold">10K Steps a Day</p>
                <p className="mt-1 text-xs opacity-80">12 days left</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Footprints, value: "8,432", label: "Steps today" },
                  { icon: Flame, value: "320", label: "Calories" },
                ].map(({ icon: Icon, value, label }) => (
                  <div
                    key={label}
                    className="rounded-2xl p-3"
                    style={{ backgroundColor: PREVIEW_NEUTRALS.surface }}
                  >
                    <Icon className="h-5 w-5" style={{ color: highlightFg }} aria-hidden="true" />
                    <p data-testid="preview-stat-value" className="mt-1 text-xl font-bold" style={{ color: main }}>
                      {value}
                    </p>
                    <p className="text-xs" style={{ color: PREVIEW_NEUTRALS.grey }}>
                      {label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Discover card */}
              <div className="rounded-2xl p-3.5" style={{ backgroundColor: PREVIEW_NEUTRALS.surface }}>
                <span
                  data-testid="preview-tag"
                  className="text-[11px] font-semibold uppercase"
                  style={{ color: highlightFg }}
                >
                  Wellness
                </span>
                <p className="mt-0.5 text-base font-semibold" style={{ color: main }}>
                  Sunrise Beach Yoga
                </p>
                <p className="text-xs" style={{ color: PREVIEW_NEUTRALS.grey }}>
                  Sat · 7:00 AM
                </p>
                <span
                  data-testid="preview-join-button"
                  className="mt-3 inline-block rounded-full px-4 py-1.5 text-[13px] font-semibold"
                  style={{ backgroundColor: highlight, color: onHighlight }}
                >
                  Join
                </span>
              </div>
            </div>

            {/* Tab bar (indicative) */}
            <div
              className="flex items-center justify-around border-t px-3 pb-6 pt-2"
              style={{ backgroundColor: PREVIEW_NEUTRALS.surface, borderColor: PREVIEW_NEUTRALS.border }}
            >
              <span
                data-testid="preview-tab-pill"
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: highlight, color: onHighlight }}
              >
                <Home className="h-4 w-4" aria-hidden="true" />
                Home
              </span>
              {[Trophy, Users, User].map((Icon, i) => (
                <Icon
                  key={i}
                  className="h-5 w-5"
                  style={{ color: PREVIEW_NEUTRALS.grey }}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground" data-testid="preview-indicative-note">
        The header is drawn exactly; the content below it is indicative.
      </p>
      <p className="text-center text-xs text-muted-foreground" data-testid="preview-caption">
        {!coloursSet
          ? "Showing the standard Athlyn look until both colours are set."
          : enabled
            ? "Light mode only — dark mode keeps the standard Athlyn look."
            : "Branding is off — employees see the standard Athlyn look."}
      </p>
    </div>
  );
}

export default BrandPreviewPhone;
